import { parseDocument } from 'htmlparser2';
import { findAll } from 'domutils';
import serialize from 'dom-serializer';

export const ELEMENT_MOTION_PRESETS = ['none', 'fade', 'slide-left', 'zoom-in', 'soft-float', 'liquid-letters', 'edge-letters', 'paper-unfold'];
export function motionStages(value) {
  const valid = preset => ELEMENT_MOTION_PRESETS.includes(preset) ? preset : 'none';
  return typeof value === 'object' && value ? { in: valid(value.in), main: value.main === 'soft-float' ? 'soft-float' : 'none', out: ['liquid-letters','edge-letters','paper-unfold'].includes(value.out) ? 'none' : valid(value.out) } : { in: valid(value), main: 'none', out: 'none' };
}
const inlineTags = new Set(['span', 'strong', 'b', 'em', 'i', 'small']);

// Animation identities are independent of text/image field identities.
export function analyzeMotionElements(html, slideIndex = 0) {
  const document = parseDocument(html);
  const elements = findAll(n => n.type === 'tag' && !['br', 'style', 'script', 'meta', 'link'].includes(n.name), document.children);
  const reserved = new Set(elements.map(element => element.attribs["data-bnb-motion-id"]).filter(Boolean));
  const used = new Set();
  const ids = new Map();
  const layers = elements.map((element, index) => {
    let id = element.attribs['data-bnb-motion-id'];
    if (!id || used.has(id)) {
      id = `slide-${slideIndex + 1}-element-${index + 1}`;
      while (used.has(id) || reserved.has(id)) id += '-new';
    }
    used.add(id);
    ids.set(element, id);
    element.attribs['data-bnb-motion-id'] = id;
    let parent = element.parent;
    while (parent && !ids.has(parent)) parent = parent.parent;
    const display = /(?:^|;)\s*display\s*:\s*([\w-]+)/i.exec(element.attribs.style || '')?.[1];
    const position = /(?:^|;)\s*position\s*:\s*(absolute|fixed)/i.test(element.attribs.style || '');
    return {
      id, slideIndex, parentId: ids.get(parent) || null,
      tag: element.name,
      bindingId: element.attribs['data-bnb-post-node'] || null,
      label: element.attribs.id ? `${element.name} #${element.attribs.id}` : `${element.name} ${index + 1}`,
      inline: !position && (display ? display === 'inline' : inlineTags.has(element.name)),
    };
  });
  return { html: serialize(document, { encodeEntities: 'utf8' }), layers };
}

export function motionLayerState(layers, bindings = []) {
  const byId = new Map(layers.map(layer => [layer.id, layer]));
  const replacing = new Set(bindings.filter(b => b.kind === 'text' && b.mode !== 'static').map(b => b.nodeId));
  return layers.map(layer => {
    let ancestor = byId.get(layer.parentId);
    while (ancestor) {
      if (replacing.has(ancestor.bindingId)) return { ...layer, blockedBy: ancestor.id };
      ancestor = byId.get(ancestor.parentId);
    }
    return { ...layer, blockedBy: null };
  });
}

// Read-time compatibility also covers old snapshots embedded in saved flows/posts.
export function normalizeTemplateMotion(template) {
  const slides = (template.slides || []).map(slide => ({ ...slide, ...analyzeMotionElements(slide.html, slide.index) }));
  const layers = slides.flatMap(slide => slide.layers);
  const states = motionLayerState(layers, template.bindings);
  const legacy = new Map((template.bindings || []).map(binding => [binding.nodeId, binding.motionPreset]));
  const animations = Object.fromEntries(states.map(layer => {
    const preset = template.animations ? template.animations[layer.id] : legacy.get(layer.bindingId);
    return [layer.id, layer.blockedBy ? 'none' : typeof preset === 'object' && preset ? motionStages(preset) : ELEMENT_MOTION_PRESETS.includes(preset) ? preset : 'none'];
  }));
  return { ...template, slides, layers, animations, motionVersion: 3, motionPreset: 'none' };
}

export function slideAnimations(template, slideIndex) {
  return template.layers.filter(layer => layer.slideIndex === slideIndex && template.animations[layer.id] !== 'none')
    .map(layer => ({ nodeId: layer.id, preset: template.animations[layer.id] }));
}
